import { apiRequest } from '../../services/apiClient';

// Structured data schemas (doc-30) — admin editor CRUD + dry runs, and the
// contract tab's read/run endpoints.

export function getSchemas() {
  return apiRequest('/schemas');
}

export function createSchema(schema) {
  return apiRequest('/schemas', { method: 'POST', json: schema });
}

export function updateSchema(id, schema) {
  return apiRequest(`/schemas/${id}`, { method: 'PUT', json: schema });
}

export function setSchemaAutomation(id, autoRun) {
  return apiRequest(`/schemas/${id}/automation`, { method: 'PUT', json: { autoRun } });
}

// Archive / restore (doc-89): first-class actions over the active flag —
// no version bump. Archived schemas hide from contract surfaces and can't
// run (batch runs skip them); outputs stay readable.
export function archiveSchema(id) {
  return apiRequest(`/schemas/${id}/archive`, { method: 'POST', json: {} });
}

export function restoreSchema(id) {
  return apiRequest(`/schemas/${id}/restore`, { method: 'POST', json: {} });
}

// Hard delete (doc-89), server-guarded: 409 while the schema has outputs on
// any contract. Takes the schema's batch-run history with it.
export function deleteSchema(id) {
  return apiRequest(`/schemas/${id}`, { method: 'DELETE' });
}

// Dry run of a (possibly unsaved) draft against a contract. Nothing is stored.
export function testRunSchema({ contractId, schema }) {
  return apiRequest('/schemas/test-run', { method: 'POST', json: { contractId, schema } });
}

// Server-side validation of a draft definition through the exact
// normalization path a save uses. Returns the normalized definition; invalid
// drafts get the same 400s a save would.
export function validateSchema(schema) {
  return apiRequest('/schemas/validate', { method: 'POST', json: schema });
}

// The formal meta-schema of the definition format (doc-74): JSON Schema +
// additionalChecks + limits. The editor's live validator reads its
// constraints; external CI fetches the same document from /api/v1.
export function getSchemaMetaSchema() {
  return apiRequest('/schemas/meta-schema');
}

// "Draft with AI" (doc-74): description + optional column headers and/or a
// sample contract → a complete server-validated definition for the editor.
export function generateSchemaDraft({ description, columnHeaders, contractId }) {
  return apiRequest('/schemas/generate', { method: 'POST', json: { description, columnHeaders, contractId } });
}

// Legacy backfill assist (doc-74): typed itemFields drafted from one
// prose-blob List/Object field.
export function generateSchemaItemFields({ field, schema }) {
  return apiRequest('/schemas/generate-item-fields', { method: 'POST', json: { field, schema } });
}

// "Fix with AI" (doc-87): the editor buffer + its active errors/warnings →
// corrected document text, shown as a diff before anything is applied.
export function fixSchemaDraft({ jsonText, findings }) {
  return apiRequest('/schemas/fix', { method: 'POST', json: { jsonText, findings } });
}

export function getContractStructuredData(contractId) {
  return apiRequest(`/contracts/${contractId}/structured-data`);
}

export function runContractSchema(contractId, schemaId, { overwriteAll = false, includeRelated } = {}) {
  return apiRequest(`/contracts/${contractId}/structured-data/${schemaId}/run`, {
    method: 'POST',
    // includeRelated is tri-state: omitted → the schema's own default.
    json: includeRelated === undefined ? { overwriteAll } : { overwriteAll, includeRelated },
  });
}

// Hand-edit one field: it becomes human-sourced (kept on rerun) and the
// output drops back to unreviewed.
export function editStructuredField(contractId, schemaId, fieldKey, value) {
  return apiRequest(`/contracts/${contractId}/structured-data/${schemaId}/fields/${encodeURIComponent(fieldKey)}`, {
    method: 'PATCH',
    json: { value },
  });
}

// Row-level operations on array fields (doc-46): a hand-added row survives
// reruns; updating an AI row locks the whole field like a per-cell edit
// always has; deleting an AI row is a local correction only.
export function addStructuredRow(contractId, schemaId, fieldKey, row) {
  return apiRequest(`/contracts/${contractId}/structured-data/${schemaId}/fields/${encodeURIComponent(fieldKey)}/rows`, {
    method: 'POST',
    json: { row },
  });
}

export function updateStructuredRow(contractId, schemaId, fieldKey, index, row) {
  return apiRequest(`/contracts/${contractId}/structured-data/${schemaId}/fields/${encodeURIComponent(fieldKey)}/rows/${index}`, {
    method: 'PUT',
    json: { row },
  });
}

export function deleteStructuredRow(contractId, schemaId, fieldKey, index) {
  return apiRequest(`/contracts/${contractId}/structured-data/${schemaId}/fields/${encodeURIComponent(fieldKey)}/rows/${index}`, {
    method: 'DELETE',
  });
}

export function verifyStructuredOutput(contractId, schemaId) {
  return apiRequest(`/contracts/${contractId}/structured-data/${schemaId}/verify`, { method: 'POST', json: {} });
}

export function searchContracts(q) {
  return apiRequest(`/contracts/search?q=${encodeURIComponent(q)}&limit=8`);
}

// Batch runs (doc-34): run one schema against many contracts. The selection is
// either { contractIds: [...] } (bulk bar) or { matching: true } (the schema's
// own targeting). Preview returns dry counts for the confirm line; create
// returns 202 with the batch to poll.
export function previewBatchRun(schemaId, selection) {
  return apiRequest(`/schemas/${schemaId}/batch-runs/preview`, { method: 'POST', json: selection });
}

// The editor's header count — the schema's own targeting, no selection body.
// GET on purpose: a page render is a read, so it writes no audit row (the
// POST above is for bulk-bar selections and IS audited).
export function previewMatchingBatchRun(schemaId) {
  return apiRequest(`/schemas/${schemaId}/batch-runs/preview`);
}

export function createBatchRun(schemaId, selection) {
  return apiRequest(`/schemas/${schemaId}/batch-runs`, { method: 'POST', json: selection });
}

export function getBatchRun(schemaId, batchId) {
  return apiRequest(`/schemas/${schemaId}/batch-runs/${batchId}`);
}
